import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { _GetSelectedProduct, type FindStoreResult } from 'autogen/swagger/all';
import { FindInStore } from 'autogen/translation-keys/trans-website-shared-buttons';
import { ButtonWithIcon, ButtonWithSpinner } from 'components/Button';
import useFindationQuickView from 'components/Findation/hooks/useFindationQuickView';
import Icon from 'components/Icon';
import useRegimeQuickView from 'components/product/regime/components/RegimeQuickView/components/RegimeQuickViewButton/hooks/useRegimeQuickView';
import { useSlides } from 'components/quickView';
import { FindInStoreProvider } from 'components/storeList/components/FindInStoreQuickView';
import { useFindInStoreQuickView } from 'components/storeList/shared/hooks';
import StoreListQuickView from 'components/storeList/storeListQuickview';
import isClient from 'helpers/isClient';
import type { EventParamDecorated } from 'modules/tracking/GA4';
import { GA4Tracking } from 'modules/tracking/GA4/GA4Tracking';
import type { IProductActionTrackingContext, ProductActionProps } from '../../shared/types';

function track(trackingInfo) {
    if (!isClient) return;

    GA4Tracking(({ getProductListParams, eventCategoryToContext }) => {
        const context = eventCategoryToContext(trackingInfo.eventCategory);
        const eventParams: EventParamDecorated[] = [];

        if (context === 'product_list') {
            eventParams.push(...getProductListParams());
        }

        return {
            eventName: 'find_in_store',
            context,
            category: 'ecommerce',
            eventParams
        };
    });
}

export function useOpenFindInStoreCallback({
    productId,
    trackingInfo
}: {
    productId: number;
    trackingInfo: IProductActionTrackingContext;
}) {
    const [, toggle] = useFindInStoreQuickView();
    const [{ isOpen: regimeQuickViewIsOpen }] = useRegimeQuickView();
    const [{ isOpen: findationQuickViewIsOpen }] = useFindationQuickView();
    const [showClickAndCollectOnly, setShowClickAndCollectOnly] = useState<boolean>(false);
    const { push } = useSlides();

    const onClickHandler = useCallback(async () => {
        await _GetSelectedProduct({ productId }).then((data) => {
            if (regimeQuickViewIsOpen || findationQuickViewIsOpen) {
                push(() => (
                    <FindInStoreProvider productId={productId} showClickAndCollectOnly={showClickAndCollectOnly}>
                        {(findInStoreResult: FindStoreResult | undefined) =>
                            data && (
                                <StoreListQuickView
                                    findStoreResult={findInStoreResult}
                                    productInfo={data}
                                    showClickAndCollectToggle={true}
                                    showClickAndCollectOnly={showClickAndCollectOnly}
                                    setShowClickAndCollectOnly={setShowClickAndCollectOnly}
                                />
                            )
                        }
                    </FindInStoreProvider>
                ));
            } else {
                toggle({
                    isOpen: true,
                    productId,
                    productInfo: data,
                    showClickAndCollectToggle: true,
                    clickAndCollectOnly: showClickAndCollectOnly,
                    showSelectParcelShop: false
                });
            }
        });

        track(trackingInfo);
    }, [productId, toggle, trackingInfo, push, regimeQuickViewIsOpen, findationQuickViewIsOpen]);

    return onClickHandler;
}

function FindInStoreButton({ params, trackingInfo, children, ...restProps }: ProductActionProps['FindInStore']) {
    const { t } = useTranslation();
    const { productId } = params;
    const onClickHandler = useOpenFindInStoreCallback({ productId, trackingInfo });

    return (
        <ButtonWithSpinner onClick={onClickHandler} variant="primary" type="button" {...restProps}>
            {children || t(FindInStore)}
        </ButtonWithSpinner>
    );
}

function FindInStoreButtonCompact({ params, trackingInfo, ...restProps }: ProductActionProps['FindInStore']) {
    const { productId } = params;
    const { t } = useTranslation();
    const onClickHandler = useOpenFindInStoreCallback({ productId, trackingInfo });

    return (
        <ButtonWithIcon
            icon={<Icon type="Pin" size={16} />}
            onClick={onClickHandler}
            variant="secondary"
            type="button"
            aria-label={t(FindInStore)}
            {...restProps}
        />
    );
}

export default Object.assign(FindInStoreButton, {
    Compact: FindInStoreButtonCompact
});
